import { CardSize } from 'omakase/ProductCard/CardBase';
import { Theme, css } from '@emotion/react';
import { assertNever } from '@tock/shared/utils/typeUtils';

const borderSize = 1;
const shadowSize = 2;
const shadowSizePx = `${shadowSize}px`;

const shadowAndBorderOffsetPx = `${shadowSize + borderSize - 1}px`;

export const cardContainer = ({ colors, interactiveStates }: Theme, size: CardSize) => [
  css`
    position: relative;
    display: flex;
    flex-direction: column;
    text-decoration: none;
    transition: all 200ms ease-in-out;
    cursor: pointer;
    height: 100%;

    > a {
      text-decoration: inherit;
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    // carousels fix for mix-blend-mode needing direct parent color to work
    .embla__slide__inner {
      background-color: ${colors.bgLighter};
    }
  `,
  size === 'small' || size === 'xsmall'
    ? css`
        display: flex;
        flex-direction: row;
      `
    : false,
];

const getTitleContainerBottomSpacingFromSize = ({ spacing }: Theme, size: CardSize) => {
  switch (size) {
    case 'large':
    case 'medium':
      return spacing(1);
    case 'small':
    case 'xsmall':
      return spacing(0.5);
    default:
      assertNever(size);
  }
};

export const cardTitleContainer = (
  theme: Theme,
  size: CardSize,
  { truncatePrimaryTextLines }: { truncatePrimaryTextLines?: number }
) => [
  css`
    margin-bottom: ${getTitleContainerBottomSpacingFromSize(theme, size)}px;
  `,
  truncatePrimaryTextLines &&
    css`
      margin-bottom: ${getTitleContainerBottomSpacingFromSize(theme, size)}px;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-box-pack: end;
      -webkit-line-clamp: ${truncatePrimaryTextLines};
      overflow: hidden;
    `,
];

export const cardBody = (
  { colors, spacing }: Theme,
  { size, includeCardBodyPadding }: { size: CardSize; includeCardBodyPadding: boolean }
) => {
  const styles = [
    css`
      background-color: ${colors.bgDefault};
      color: ${colors.fgDefault};
    `,
  ];
  switch (size) {
    case 'large':
      styles.push(css`
        padding: ${spacing(3.5)}px ${spacing(includeCardBodyPadding ? 3 : 0)}px;
        display: flex;
        flex-direction: column;
        height: 100%;
      `);
      break;
    case 'medium':
      styles.push(css`
        padding: ${spacing(2)}px 0;
      `);
      break;
    case 'xsmall':
      styles.push(css`
        flex: 1;
      `);
      break;
    default:
      break;
  }
  return styles;
};

export const imageContainer = (theme: Theme, size: CardSize) => {
  return [
    size === 'small' &&
      css`
        width: 100px;
        margin-right: ${theme.spacing(2)}px;
      `,
    size === 'xsmall' &&
      css`
        width: 76px;
        margin-right: ${theme.spacing(2)}px;
      `,
  ];
};

export const cardLink = ({ interactiveStates, boxShadows, colors, zIndex }: Theme) => [
  css`
    text-decoration: none;
    outline: none;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    position: absolute;
    z-index: ${zIndex.bump};

    &::after {
      bottom: ${shadowAndBorderOffsetPx};
      box-shadow: 0 0 0 ${shadowSizePx} transparent;
      content: '';
      left: ${shadowAndBorderOffsetPx};
      pointer-events: none;
      position: absolute;
      right: ${shadowAndBorderOffsetPx};
      top: ${shadowAndBorderOffsetPx};
    }
  `,
  interactiveStates.focus(css`
    box-shadow: ${boxShadows.dropShadowNone};
    &::after {
      box-shadow: 0 0 0 ${shadowSizePx} ${colors.borderPrimaryBase};
    }
  `),
];

export const quantityPickerContainer = ({ zIndex }: Theme, size: CardSize) => [
  css`
    position: relative;
    z-index: ${zIndex.bump};
  `,
  size === 'large' &&
    css`
      position: absolute;
      right: 0;
    `,
];

export const descriptionTxt = (lines: number) => css`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${lines};
  -webkit-box-pack: end;
  overflow: hidden;
`;

export const lineThroughText = css`
  text-decoration-line: line-through;
`;

export const getPrimaryVariantFromSize = (size: CardSize) => {
  switch (size) {
    case 'large':
    case 'medium':
      return 'heading04';
    case 'small':
    case 'xsmall':
      return 'body02Strong';
    default:
      assertNever(size);
  }
};

export const getOverlineVariantFromSize = (size: CardSize) => {
  switch (size) {
    case 'large':
    case 'medium':
      return 'body02Strong';
    case 'small':
    case 'xsmall':
      return 'captionStrong';
    default:
      assertNever(size);
  }
};

export const getBodyVariantFromSize = (size: CardSize) => {
  switch (size) {
    case 'large':
    case 'medium':
      return 'body02';
    case 'small':
    case 'xsmall':
      return 'caption';
    default:
      assertNever(size);
  }
};
